import { useCallback, useMemo, useState } from 'react';
import { getStateFromIsoDate, getIsoDateFromState } from '../../utils/datetimeUtils';
import * as SimpleDate from 'foundations-components/transitional/utils/SimpleDate';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIDateInput from 'UIComponents/dates/UIDateInput';
import UITimeInput from 'UIComponents/input/UITimeInput';
import { Grid, GridItem } from 'foundations-components/transitional/components/Grid';
import { useViewport } from '../../../../hooks/useViewport';
import { TextInput } from 'foundations-components/transitional/components/TextInput';
import UIFormControl from 'UIComponents/form/UIFormControl';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TIMEZONE_REGEX = /^[+-]\d\d:\d\d$/m;
export default function DateTimeInput({
  value,
  onChange,
  disabled,
  id,
  readOnly,
  autoFocus
}) {
  const [state, setState] = useState(() => {
    return Object.assign({}, getStateFromIsoDate(value), {
      timezoneError: false
    });
  });
  const {
    isMobile
  } = useViewport();
  const size = useMemo(() => {
    // use the `isMobile` variable from the viewport to determine the screen size (vs letting GridItem determine the mobile size)
    return isMobile ? {
      xs: 12
    } : {
      xs: 4
    };
  }, [isMobile]);
  const update = useCallback((newState = {}) => {
    const existingValues = Object.assign({}, state, newState);
    const dateToSet = {
      date: existingValues.date || SimpleDate.now(),
      time: existingValues.time || 0,
      timezone: existingValues.timezone || '+00:00'
    };
    setState(dateToSet);
    const isoDate = getIsoDateFromState(dateToSet);
    onChange(isoDate);
  }, [state, onChange]);
  const validateTimezoneAndUpdate = useCallback(inputState => {
    var _inputState$timezone;
    if (!((_inputState$timezone = inputState.timezone) !== null && _inputState$timezone !== void 0 && _inputState$timezone.match(TIMEZONE_REGEX))) {
      setState(Object.assign({}, inputState, {
        timezoneError: true
      }));
    } else {
      update(Object.assign({}, inputState, {
        timezoneError: false
      }));
    }
  }, [update]);
  const onDateChange = useCallback(e => {
    const dateValue = e.target.value;
    setState(prev => Object.assign({}, prev, {
      date: dateValue
    }));
    update({
      date: dateValue
    });
  }, [update]);
  const onTimeChange = useCallback(e => {
    const targetValue = Number(e.target.value);
    setState(prev => Object.assign({}, prev, {
      time: targetValue
    }));
    update({
      time: targetValue
    });
  }, [update]);
  const onTimeZoneChange = e => {
    validateTimezoneAndUpdate(Object.assign({}, state, {
      timezone: e.target.value
    }));
  };
  return /*#__PURE__*/_jsxs(Grid, {
    children: [/*#__PURE__*/_jsx(GridItem, {
      size: size,
      children: /*#__PURE__*/_jsx(UIDateInput, {
        disabled: disabled,
        id: `${id}-date`,
        onChange: onDateChange,
        readOnly: readOnly,
        autoFocus: autoFocus,
        value: state.date
      })
    }), /*#__PURE__*/_jsx(GridItem, {
      padding: isMobile ? undefined : '0',
      marginBlockEnd: isMobile ? '200' : undefined,
      marginBlockStart: isMobile ? '200' : undefined,
      size: size,
      children: /*#__PURE__*/_jsx(UITimeInput, {
        disabled: disabled,
        id: `${id}-time`,
        onChange: onTimeChange,
        readOnly: readOnly,
        value: state.time
      })
    }), /*#__PURE__*/_jsx(GridItem, {
      size: size,
      children: /*#__PURE__*/_jsx(UIFormControl, {
        "aria-label": I18n.text('developer-docs-shared-components.params.body.dateTime.timezoneLabel'),
        error: state.timezoneError,
        validationMessage: state.timezoneError ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "developer-docs-shared-components.params.body.dateTime.timezoneValidationError"
        }) : null,
        children: /*#__PURE__*/_jsx(TextInput, {
          onChange: onTimeZoneChange,
          placeholder: "+00:00",
          value: state.timezone
        })
      })
    })]
  });
}